<template>
  <e-dialog ref="dialogRef" @open="handleDialogOpen" @after-leave="handleDialogAfterLeave">
    <template #title>
      <div class="caption">{{ isEdit ? `#${editedRestriction?.restriction.number}` : 'Новое ограничение' }}</div>
    </template>

    <period-overlap-conflict-alert v-model="form.hasPeriodOverlapConflict" class="mb-6" />

    <e-form v-model="form.isValid" @submit="handleSubmit">
      <div class="d-flex align-start ga-4">
        <e-date-picker
          v-model="form.period.from"
          label="Период с"
          :min="periodFromMin"
          :max="form.period.to"
          :disabled="fromInThePast"
          :error-messages="periodFromErrorMessage"
        />
        <e-date-picker
          v-model="form.period.to"
          label="Период по"
          :min="dayjs.max(form.period.from, dayjs(new Date()))"
          :max="periodToMax"
          :error-messages="periodToErrorMessage"
        />
      </div>
      <e-select
        v-if="form.items.length === 1"
        v-model="form.items[0].serviceGroup"
        class="mt-6"
        label="Группа услуг"
        :items="serviceGroupItems"
        item-title="restrictionFactor.name"
        item-value="restrictionFactor.cid"
        :disabled="!!props.selectedCells.length"
      />

      <div class="paragraph-l font-600 mt-6">Настройка ограничения</div>
      <div class="paragraph-s text-secondary mt-1 mb-6">
        <template v-if="form.items.length === 1">Настройки должны отличаться от настроек по умолчанию</template>
        <template v-else>Ограничения с настройками по умолчанию не будут созданы</template>
      </div>

      <template v-if="form.items.length === 1">
        <e-radio-group v-model="form.items[0].isOpened" :items="isOpenedRadioItems" inline />
        <e-checkbox
          v-model="form.items[0].isNeedConfirm"
          label="Бронь под запрос"
          class="mt-6"
          :disabled="!form.items[0].isOpened"
        />
        <e-checkbox
          v-model="form.items[0].isDeclineForPeriod"
          label="Запрет заезда на период"
          class="mt-6"
          :disabled="!form.items[0].isOpened"
          @update:model-value="handleDeclineForPeriodChange(0)"
        />
        <div class="d-flex align-start ga-4 mt-6">
          <e-input
            v-model:number-value="form.items[0].minOrderPeriod"
            :maska="ORDER_PERIOD_MASKA"
            :rules="[min(1), max(100)]"
            :error-messages="getItemMinOrderPeriodErrorMessage(0)"
            label="Мин. кол-во ночей"
            :disabled="!form.items[0].isOpened || form.items[0].isDeclineForPeriod"
          />
          <e-input
            v-model:number-value="form.items[0].maxOrderPeriod"
            :maska="ORDER_PERIOD_MASKA"
            :rules="[min(1), max(100)]"
            :error-messages="getItemMaxOrderPeriodErrorMessage(0)"
            label="Макс. кол-во ночей"
            :disabled="!form.items[0].isOpened || form.items[0].isDeclineForPeriod"
          />
        </div>
      </template>
      <div v-else class="d-grid gr-8">
        <group-operation-block
          v-for="(item, index) in form.items"
          :key="index"
          :error="checkIsItemDefault(index) ? 'Ограничение не будет создано' : ''"
          @remove="handleItemRemove(index)"
        >
          <template #title>
            {{ getServiceGroupNameById(item.serviceGroup) }}
          </template>
          <e-radio-group v-model="item.isOpened" :items="isOpenedRadioItems" inline />
          <e-checkbox v-model="item.isNeedConfirm" label="Бронь под запрос" class="mt-6" :disabled="!item.isOpened" />
          <e-checkbox
            v-model="item.isDeclineForPeriod"
            label="Запрет заезда на период"
            class="mt-6"
            :disabled="!item.isOpened"
          />
          <div class="d-flex ga-4 mt-6">
            <e-input
              v-model:number-value="item.minOrderPeriod"
              :maska="ORDER_PERIOD_MASKA"
              :rules="[min(1), max(100)]"
              :error-messages="getItemMinOrderPeriodErrorMessage(index)"
              label="Мин. кол-во ночей"
              :disabled="!item.isOpened || item.isDeclineForPeriod"
            />
            <e-input
              v-model:number-value="item.maxOrderPeriod"
              :maska="ORDER_PERIOD_MASKA"
              :rules="[min(1), max(100)]"
              :error-messages="getItemMaxOrderPeriodErrorMessage(index)"
              label="Макс. кол-во ночей"
              :disabled="!item.isOpened || item.isDeclineForPeriod"
            />
          </div>
        </group-operation-block>
      </div>

      <div class="d-flex align-center mt-6">
        <remove-restriction-dialog
          v-if="isEdit"
          :number="editedRestriction?.restriction.number"
          :action="removeRestrictionAction"
          :operation-delete="serviceGroupsRestrictions?.operations.remove"
          with-activator
        />
        <e-btn variant="secondary" class="mr-4 ml-auto" @click="handleCancel">Отмена</e-btn>
        <e-btn variant="primary" type="submit" :disabled="isSaveButtonDisabled" :loading="form.isLoading">
          {{ isEdit ? 'Сохранить' : 'Создать' }}
        </e-btn>
      </div>
    </e-form>
  </e-dialog>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import type { ChessDateCellType } from '@/core/components/interface/Chess/Chess.type';
import type { RangeOfDateOnly } from '@/graphql/generated';
import type {
  ServiceGroupsRestrictionsCellDataType,
  ServiceGroupsRestrictionsTableItemType,
} from '@/parts/tariffs/store/restrictions';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn, ECheckbox, EDatePicker, EForm, EInput, ERadioGroup, ESelect } from '@/core/components/ui';
import { useSnackbar } from '@/core/composables/useSnackbar';
import { getArrayMinMax } from '@/core/utils/array';
import { getDateWithUtcOffset, getIsoDateFormat, getIsoDateWithUtcOffset, isDayBefore } from '@/core/utils/date';
import { max, min, required } from '@/core/utils/validation';
import { errorTypes } from '@/parts/errors/utils/error-types';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import GroupOperationBlock from '@/parts/tariffs/components/PricesAndRestrictions/GroupOperationBlock.vue';
import PeriodOverlapConflictAlert from '@/parts/tariffs/components/PricesAndRestrictions/PeriodOverlapConflictAlert.vue';
import RemoveRestrictionDialog from '@/parts/tariffs/components/PricesAndRestrictions/RemoveRestrictionDialog.vue';
import { useRestrictionsStore } from '@/parts/tariffs/store/restrictions';

const ORDER_PERIOD_MASKA = '###';

const isOpenedRadioItems = [
  { title: 'Продажа открыта', value: true },
  { title: 'Продажа закрыта', value: false },
];

const props = defineProps<{
  isEditAvailable?: boolean;
  selectedCells: ChessDateCellType<ServiceGroupsRestrictionsCellDataType>[];
}>();

const $route = useRoute();
const { showSnackbar } = useSnackbar();
const { timeZoneOffsetMin } = useAccommodationObjectsStore();
const {
  serviceGroupsRestrictions,
  serviceGroupsRestrictionsChessPeriod,
  getServiceGroupsRestrictions,
  addServiceGroupsRestriction,
  updateServiceGroupsRestriction,
  removeServiceGroupsRestriction,
} = useRestrictionsStore();

const dialogRef = ref();

const form = reactive<{
  period: RangeOfDateOnly;
  items: Array<{
    serviceGroup: string | null;
    isOpened: boolean;
    isNeedConfirm: boolean;
    isDeclineForPeriod: boolean;
    minOrderPeriod: number | null;
    maxOrderPeriod: number | null;
  }>;

  isValid: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  hasPeriodOverlapConflict: boolean;
}>({
  period: { from: '', to: '' },
  items: [],

  isValid: false,
  isLoading: false,
  isSuccess: false,
  hasPeriodOverlapConflict: false,
});

const fromInThePast = computed(() => {
  return form.period.from && isDayBefore(form.period.from, new Date());
});

const isEdit = ref(false);
const editedRestriction = ref<ServiceGroupsRestrictionsTableItemType>();

const tariffScheduleCid = computed(() => String($route.params.t_id));

const serviceGroupItems = computed(() => serviceGroupsRestrictions.value?.grid ?? []);

const periodFromMin = computed(() => {
  const todayWithTimeZoneOffset = getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin.value);
  if (serviceGroupsRestrictionsChessPeriod.value?.from > todayWithTimeZoneOffset) {
    return getDateWithUtcOffset(serviceGroupsRestrictionsChessPeriod.value?.from, timeZoneOffsetMin.value);
  }
  if (editedRestriction.value && editedRestriction.value.restriction.period.from < todayWithTimeZoneOffset) {
    return getDateWithUtcOffset(editedRestriction.value.restriction.period.from, timeZoneOffsetMin.value);
  }
  return getDateWithUtcOffset(todayWithTimeZoneOffset, timeZoneOffsetMin.value);
});

const periodToMax = computed(() => {
  return getDateWithUtcOffset(serviceGroupsRestrictionsChessPeriod.value?.to, timeZoneOffsetMin.value);
});

const isSaveButtonDisabled = computed(() => {
  if (!props.isEditAvailable) {
    return true;
  }

  if (!form.isValid) {
    return true;
  }

  if (!form.items.some((item, index) => item.serviceGroup && !checkIsItemDefault(index))) {
    return true;
  }

  if (editedRestriction.value) {
    const item = form.items[0];
    const restriction = editedRestriction.value.restriction;

    if (
      ((item.isOpened &&
        restriction.options.isOpened &&
        item.isNeedConfirm === (restriction.options.options?.isNeedConfirm ?? false) &&
        item.isDeclineForPeriod === !restriction.options.options?.orderPeriod &&
        item.minOrderPeriod === (restriction.options.options?.orderPeriod?.minOrderPeriod ?? null) &&
        item.maxOrderPeriod === (restriction.options.options?.orderPeriod?.maxOrderPeriod ?? null)) ||
        (!item.isOpened && !restriction.options.isOpened)) &&
      getIsoDateFormat(form.period.from) === restriction.period.from &&
      getIsoDateFormat(form.period.to) === restriction.period.to
    ) {
      return true;
    }
  }

  return false;
});

const handleDeclineForPeriodChange = (index: number) => {
  if (form.items[index].isDeclineForPeriod) {
    form.items[index].minOrderPeriod = null;
    form.items[index].maxOrderPeriod = null;
  }
};

const periodFromErrorMessage = computed(() => {
  const periodFrom = dayjs(form.period.from).format('YYYY-MM-DD');
  const periodTo = dayjs(form.period.to).format('YYYY-MM-DD');

  if (
    serviceGroupsRestrictions.value?.table.some((item) => {
      return (
        item.restriction.period.to >= periodFrom &&
        item.restriction.period.to <= periodTo &&
        item.restriction.restrictionId !== editedRestriction.value?.restriction.restrictionId &&
        form.items.some((formItem) => formItem.serviceGroup === item.restrictionFactor.cid) &&
        !form.isLoading &&
        !form.isSuccess
      );
    })
  ) {
    return 'Наложение дат с существующим ограничением';
  }

  return '';
});

const periodToErrorMessage = computed(() => {
  const periodFrom = dayjs(form.period.from).format('YYYY-MM-DD');
  const periodTo = dayjs(form.period.to).format('YYYY-MM-DD');

  if (
    serviceGroupsRestrictions.value?.table.some((item) => {
      return (
        item.restriction.period.from >= periodFrom &&
        item.restriction.period.from <= periodTo &&
        item.restriction.restrictionId !== editedRestriction.value?.restriction.restrictionId &&
        form.items.some((formItem) => formItem.serviceGroup === item.restrictionFactor.cid) &&
        !form.isLoading &&
        !form.isSuccess
      );
    })
  ) {
    return 'Наложение дат с существующим ограничением';
  }

  return '';
});

const getDefaultOptions = (serviceGroupCid?: string | null) => {
  return serviceGroupsRestrictions.value?.defaults.find((item) => {
    return item.restrictionFactorKey.serviceGroupCid === serviceGroupCid;
  })?.defaultOptions;
};

const getServiceGroupNameById = (id: string | null) => {
  return serviceGroupItems.value.find((item) => item.restrictionFactor.cid === id)?.restrictionFactor.name || '';
};

const handleDialogOpen = () => {
  if (props.selectedCells.length) {
    const editedRestrictionId = props.selectedCells.find((item) => item.data?.restrictionId)?.data?.restrictionId;

    if (editedRestrictionId) {
      editedRestriction.value = serviceGroupsRestrictions.value?.table.find(
        (item) => item.restriction.restrictionId === editedRestrictionId,
      );
      form.period = {
        from: getDateWithUtcOffset(editedRestriction.value?.restriction.period.from, timeZoneOffsetMin.value),
        to: getDateWithUtcOffset(editedRestriction.value?.restriction.period.to, timeZoneOffsetMin.value),
      };
      form.items = [
        {
          serviceGroup: editedRestriction.value?.restrictionFactor.cid ?? null,
          isOpened: editedRestriction.value?.restriction.options.isOpened ?? true,
          isNeedConfirm: editedRestriction.value?.restriction.options.options?.isNeedConfirm ?? false,
          isDeclineForPeriod: !editedRestriction.value?.restriction.options.options?.orderPeriod,
          minOrderPeriod: editedRestriction.value?.restriction.options.options?.orderPeriod?.minOrderPeriod ?? null,
          maxOrderPeriod: editedRestriction.value?.restriction.options.options?.orderPeriod?.maxOrderPeriod ?? null,
        },
      ];
      isEdit.value = true;
    } else {
      const periodFromTo = getArrayMinMax(props.selectedCells.map((item) => item.date));
      form.period = {
        from: getDateWithUtcOffset(periodFromTo[0], timeZoneOffsetMin.value),
        to: getDateWithUtcOffset(periodFromTo[1], timeZoneOffsetMin.value),
      };
      props.selectedCells.forEach((cell) => {
        if (!form.items.some((item) => item.serviceGroup === cell.data?.serviceGroup)) {
          const defaultOptions = getDefaultOptions(cell.data?.serviceGroup);

          form.items.push({
            serviceGroup: cell.data?.serviceGroup ?? null,
            isOpened: defaultOptions?.isOpened ?? true,
            isNeedConfirm: defaultOptions?.options?.isNeedConfirm ?? false,
            isDeclineForPeriod: !defaultOptions?.options?.orderPeriod,
            minOrderPeriod: defaultOptions?.options?.orderPeriod?.minOrderPeriod ?? null,
            maxOrderPeriod: defaultOptions?.options?.orderPeriod?.maxOrderPeriod ?? null,
          });
        }
      });
    }
  } else {
    const defaultOptions = serviceGroupsRestrictions.value?.defaults[0].defaultOptions;

    form.period = {
      from: getDateWithUtcOffset(periodFromMin.value, timeZoneOffsetMin.value),
      to: getDateWithUtcOffset(periodToMax.value, timeZoneOffsetMin.value),
    };
    form.items = [
      {
        serviceGroup: null,
        isOpened: defaultOptions?.isOpened ?? true,
        isNeedConfirm: defaultOptions?.options?.isNeedConfirm ?? false,
        isDeclineForPeriod: !defaultOptions?.options?.orderPeriod,
        minOrderPeriod: defaultOptions?.options?.orderPeriod?.minOrderPeriod ?? null,
        maxOrderPeriod: defaultOptions?.options?.orderPeriod?.maxOrderPeriod ?? null,
      },
    ];
  }
};

const handleDialogAfterLeave = () => {
  form.period = { from: '', to: '' };
  form.items = [];
  form.isSuccess = false;
  isEdit.value = false;
  editedRestriction.value = undefined;
  if (form.hasPeriodOverlapConflict) {
    getServiceGroupsRestrictions(tariffScheduleCid.value);
    form.hasPeriodOverlapConflict = false;
  }
};

const getItemMinOrderPeriodErrorMessage = (index: number) => {
  const item = form.items[index];

  if (item.minOrderPeriod && item.maxOrderPeriod && item.minOrderPeriod > item.maxOrderPeriod) {
    return 'Значение не может быть больше макс. кол-ва ночей';
  }

  if (form.items[index].isOpened && !form.items[index].isDeclineForPeriod) {
    return required()(form.items[index].minOrderPeriod);
  }

  return '';
};

const getItemMaxOrderPeriodErrorMessage = (index: number) => {
  const item = form.items[index];

  if (item.minOrderPeriod && item.maxOrderPeriod && item.maxOrderPeriod < item.minOrderPeriod) {
    return 'Значение не может быть меньше мин. срока';
  }

  return '';
};

const checkIsItemDefault = (index: number) => {
  const defaultOptions = getDefaultOptions(form.items[index].serviceGroup);

  const defaultIsOpened = defaultOptions?.isOpened ?? true;
  const defaultIsNeedConfirm = defaultOptions?.options?.isNeedConfirm ?? false;
  const defaultIsDeclineForPeriod = !defaultOptions?.options?.orderPeriod;
  const defaultMinOrderPeriod = defaultOptions?.options?.orderPeriod?.minOrderPeriod ?? null;
  const defaultMaxOrderPeriod = defaultOptions?.options?.orderPeriod?.maxOrderPeriod ?? null;

  if (
    form.items[index].isOpened !== defaultIsOpened ||
    form.items[index].isNeedConfirm !== defaultIsNeedConfirm ||
    form.items[index].isDeclineForPeriod !== defaultIsDeclineForPeriod ||
    form.items[index].minOrderPeriod !== defaultMinOrderPeriod ||
    form.items[index].maxOrderPeriod !== defaultMaxOrderPeriod
  ) {
    return false;
  }

  return true;
};

const handleItemRemove = (index: number) => {
  form.items.splice(index, 1);
};

const handleSubmit = async () => {
  if (!form.isValid) return;

  const period = {
    from: dayjs(form.period.from).format('YYYY-MM-DD'),
    to: dayjs(form.period.to).format('YYYY-MM-DD'),
  };

  form.isLoading = true;

  if (editedRestriction.value) {
    const result = await updateServiceGroupsRestriction({
      options: {
        isOpened: form.items[0].isOpened,
        options: form.items[0].isOpened
          ? {
              isNeedConfirm: form.items[0].isNeedConfirm,
              orderPeriod: !form.items[0].isDeclineForPeriod
                ? {
                    minOrderPeriod: form.items[0].minOrderPeriod,
                    maxOrderPeriod: form.items[0].maxOrderPeriod,
                  }
                : undefined,
            }
          : undefined,
      },
      period,
      restrictionId: editedRestriction.value.restriction.restrictionId,
      tariffScheduleCid: tariffScheduleCid.value,
    });

    if (!result?.errors) {
      form.isSuccess = true;
      dialogRef.value.close();

      showSnackbar({ type: 'success', title: 'Ограничение ГУ изменено' });
    }
  } else {
    const result = await addServiceGroupsRestriction({
      restrictions: form.items
        .filter((_, index) => !checkIsItemDefault(index))
        .map((item) => ({
          options: {
            isOpened: item.isOpened,
            options: item.isOpened
              ? {
                  isNeedConfirm: item.isNeedConfirm,
                  orderPeriod: !item.isDeclineForPeriod
                    ? {
                        minOrderPeriod: item.minOrderPeriod,
                        maxOrderPeriod: item.maxOrderPeriod,
                      }
                    : undefined,
                }
              : undefined,
          },
          period,
          serviceGroupCid: item.serviceGroup ?? '',
        })),
      tariffScheduleCid: tariffScheduleCid.value,
    });

    if (!result?.errors) {
      form.isSuccess = true;
      dialogRef.value.close();

      showSnackbar({ type: 'success', title: 'Ограничение ГУ создано' });
    } else if (result.errors[0].__typename === errorTypes.RestrictionPeriodOverlapConflictError) {
      form.hasPeriodOverlapConflict = true;
    }
  }

  form.isLoading = false;
};

const removeRestrictionAction = async () => {
  if (!editedRestriction.value) return;

  await removeServiceGroupsRestriction({
    restrictionId: editedRestriction.value.restriction.restrictionId,
    tariffScheduleCid: tariffScheduleCid.value,
  });
  dialogRef.value.close();
};

const handleCancel = () => {
  dialogRef.value.close();
};
</script>
